import React from 'react';
import styled, { ThemeProvider } from 'styled-components';
import { useRouter } from 'next/router';
import {
  cssColorValue,
  Type,
  FormSelect,
  black,
  white,
  screenSmMax,
  spacer8,
  spacer16,
  spacer20,
  spacer40,
  spacer24,
  body1FontSize,
} from '@spotify-internal/encore-web';
import { lightTheme } from '../Theme';
import type { SearchHit } from '../Search/types';
import { ContentItem } from './ContentItem';
import { Filter } from './filterType';
import { useT } from '../../../features/src/i18n';

const ListWrapper = styled.div`
  padding: 0px;
`;

const ContentWrapper = styled.div`
  padding: 0px;
  width: 100%;

  :nth-child(odd) {
    background: ${cssColorValue('decorativeSubdued')};
  }

  :nth-child(even) {
    background: ${cssColorValue('backgroundHighlight')};
  }
`;

const FilterBarWrapper = styled.div<{
  filterBarColor?: string;
}>`
  width: 100%;
  background: ${({ filterBarColor }) => filterBarColor};
  padding: ${spacer20} ${spacer40};
  display: flex;
  flex-direction: row;
  justify-content: space-between;

  @media (max-width: ${screenSmMax}) {
    flex-direction: column;
    padding: ${spacer16};
  }

  .SelectContainer {
    width: 30%;
  }
`;

const Headline = styled(Type.h1).attrs({
  paddingBottom: '0.75em',
  variant: 'alto',
})<{
  filterBarColor?: string;
}>`
  color: ${({ filterBarColor }) =>
    filterBarColor === cssColorValue('decorativeSubdued') ? black : white};
  padding-top: ${spacer24};

  @media (max-width: ${screenSmMax}) {
    padding: 0px;
    width: 100%;
  }
`;

const FilterWrapper = styled.div<{
  filterBarColor?: string;
}>`
  color: ${({ filterBarColor }) =>
    filterBarColor === cssColorValue('decorativeSubdued') ? black : white};

  @media (max-width: ${screenSmMax}) {
    flex-direction: column;
    padding-top: ${spacer8};
    width: 70%;
  }
`;

const Filters = styled.div`
  display: flex;
  flex-direction: row;
`;

const DropDownBox = styled.div`
  padding-right: ${spacer20};
  select,
  input {
    font-size: ${body1FontSize};
  }

  &:last-child {
    padding: 0px;
  }
`;

const FilterHeader = styled(Type.p).attrs({
  variant: 'minuetBold',
})``;

type ContentListType = {
  filterBarTitle: string;
  items?: SearchHit[];
  filters?: Filter[];
  filterBarColor?: string;
  showFilters?: boolean;
  emptyMessage?: string;
};

const forceEventsList = [
  {
    title: 'Test Data',
    description: 'Test data to enable LQA',
    franchise: 'NONE',
    image: undefined,
    date: '2900-08-28',
    location: 'Atlanta, USA',
    url: 'https://www.thewilliamsburghotel.com/pool',
    id: '1',
    contentType: 'event',
    urlSlug: 'cao',
    isSearchHit: false,
    score: -1,
  } as SearchHit,
];

export const ContentList: React.FC<ContentListType> = props => {
  const t = useT();
  const router = useRouter();
  const emptyTranslation = t(
    'FRODOR_8ea642',
    'There are no items matching the specified filters.',
    '',
  );
  const {
    filterBarTitle = '',
    filters,
    filterBarColor = black,
    showFilters = true,
  } = props;
  let { items = [], emptyMessage = emptyTranslation } = props;
  if (router && router.query && router.query._lqa === 'showContentListEmpty') {
    emptyMessage = emptyTranslation;
  }
  if (router && router.query && router.query._lqa === 'showEvents') {
    items = forceEventsList;
  }
  const [itemList, setItemList] = React.useState<SearchHit[]>(items);

  function filterDropdown(formValue: string, filterValue: string) {
    // @ts-ignore TODO - how to fix this linting error?
    if (formValue !== 'all') {
      const filteredItems = items.filter(function dropdown(item) {
        // @ts-ignore TODO - how to fix this linting error?
        return item[filterValue] === formValue;
      });
      setItemList(filteredItems);
    } else {
      setItemList(items);
    }
  }

  return (
    <ThemeProvider theme={lightTheme}>
      <ListWrapper>
        <FilterBarWrapper filterBarColor={filterBarColor}>
          <Headline filterBarColor={filterBarColor}>{filterBarTitle}</Headline>
          {filters && showFilters && (
            <FilterWrapper filterBarColor={filterBarColor}>
              <FilterHeader>
                {t(
                  'FRODOR_CONTENT_LIST_SHOW_FILTER',
                  'Show',
                  'Show refers to an event',
                )}
              </FilterHeader>
              <Filters>
                {filters.map((filter, key) => (
                  <DropDownBox key={key}>
                    <FormSelect
                      defaultValue="all"
                      onChange={e => {
                        // @ts-ignore TODO How to remove this linting error?
                        filterDropdown(e.target.value, filter.filterValue);
                      }}
                      data-testid={`dropdown-${filter.filterValue}`}
                    >
                      <option value="all">
                        {filter.allLabel ||
                          filter.filterValue.charAt(0).toUpperCase() +
                            filter.filterValue.slice(1)}
                      </option>
                      {filter.filterOptions.map((filterOption, index) => (
                        <option
                          key={index}
                          value={filterOption}
                          data-testid={`option-${filterOption}`}
                        >
                          {filterOption}
                        </option>
                      ))}
                    </FormSelect>
                  </DropDownBox>
                ))}
              </Filters>
            </FilterWrapper>
          )}
        </FilterBarWrapper>

        {(itemList.length === 0 ||
          (router &&
            router.query &&
            router.query._lqa === 'showContentListEmpty')) && (
          <ContentWrapper>
            <ContentItem>{emptyMessage}</ContentItem>
          </ContentWrapper>
        )}

        {itemList.map((item, index) => (
          <ContentWrapper key={index}>
            <ContentItem {...item} />
          </ContentWrapper>
        ))}
      </ListWrapper>
    </ThemeProvider>
  );
};
